import { ReactElement, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Divider, Drawer, IconButton, Menu, MenuItem, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Printer, X } from "lucide-react";
import { useReactToPrint } from "react-to-print";
import OrderDetailsTab from "./OrderDetailsTab";
import OrderClientTab from "./OrderClientTab";
import OrderTimer from "./OrderTimer";
import useRoutes from "@/hooks/useRoutes";
import OrderTicket from "./OrderTicket";
import KitchenOrder from "./KitchenOrder";
import { Order } from "@/types";

const INITIAL_TAB = "details";

const OrderDetails = (order: Order) => {
  const OrderTicketRef = useRef(null);
  const kitchenOrderRef = useRef(null);
  const [navigate] = useRoutes();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [activeTab, setActiveTab] = useState<Tabs>(INITIAL_TAB);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const tabs: Record<
    string,
    {
      label: string;
      component: ReactElement;
    }
  > = {
    details: {
      label: "Detalles",
      component: <OrderDetailsTab {...order} />,
    },
    clientInfo: {
      label: "Cliente",
      component: <OrderClientTab {...order} />,
    },
  };

  type Tabs = keyof typeof tabs;

  const handleChange = (_event: React.SyntheticEvent, newValue: Tabs) => {
    setActiveTab(newValue);
  };

  const handleClose = () => {
    navigate("organizations.orders.main", { organizationId: organizationId ?? "" });
  };

  const printOrder = useReactToPrint({
    contentRef: OrderTicketRef,
    documentTitle: "Factura",
    onAfterPrint: () => setAnchorEl(null),
  });

  const printKitchenOrder = useReactToPrint({
    contentRef: kitchenOrderRef,
    documentTitle: "Comanda",
    onAfterPrint: () => setAnchorEl(null),
  });

  return (
    <Stack p={2}>
      <Drawer open={!!order.id} onClose={handleClose}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            padding: 2,
            paddingBottom: 0,
          }}
        >
          <Typography variant="h2">{`Pedido / ${order.number}`}</Typography>
          <Stack direction="row" alignItems="center " spacing={2}>
            <OrderTimer starts={order?.orderedAt ?? order?.createdAt} ends={order?.completedAt} />
            <IconButton color="primary" size="large" component={Paper} elevation={2} onClick={handleClick} sx={{ backgroundColor: "#ffffff" }}>
              <Printer />
            </IconButton>
            <Menu
              id="printer-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              MenuListProps={{
                "aria-labelledby": "printer-button",
              }}
            >
              <MenuItem onClick={printOrder}>Factura</MenuItem>
              <MenuItem onClick={printKitchenOrder}>Comanda</MenuItem>
            </Menu>
            <IconButton color="primary" size="large" component={Paper} elevation={2} onClick={handleClose} sx={{ backgroundColor: "#ffffff" }}>
              <X />
            </IconButton>
          </Stack>
        </Stack>

        <Tabs value={activeTab} onChange={handleChange} sx={{ paddingX: 2 }}>
          {Object.entries(tabs).map(([tabKey, tab]) => (
            <Tab key={tabKey} label={tab.label} value={tabKey} />
          ))}
        </Tabs>
        <Divider />
        <Stack spacing={2} p={2} flex={1}>
          {tabs[activeTab].component}
        </Stack>
      </Drawer>
      <div ref={OrderTicketRef}>
        <OrderTicket order={order} />
      </div>
      <div ref={kitchenOrderRef}>
        <KitchenOrder order={order} />
      </div>
    </Stack>
  );
};

export default OrderDetails;
